import { HttpClient } from '@angular/common/http';
import { toHttpParams } from './http-params';
import { Injectable, inject } from '@angular/core';
import type { Observable } from 'rxjs';

/** Tenant kind, as returned by the API (PascalCase enum name). */
export type TenantTypeName = 'Sameie' | 'Borettslag';

/** Tenant lifecycle state, as returned by the API. */
export type TenantStatusName = 'Kladd' | 'Aktiv' | 'Suspendert' | 'Arkivert';

/** A row in the portfolio list (`GET /api/admin/tenants`). */
export interface TenantSummary {
  id: string;
  name: string;
  type: TenantTypeName;
  status: TenantStatusName;
  unitCount: number;
  provisionedAtUtc: string | null;
  /** Subscription plan label (`start` | `standard` | `komplett` | `forvalter`). */
  plan: string;
  /** City of the first address; null when none. */
  place: string | null;
  /** Forvalter name — null until the tenant carries a forvalter link (A4). */
  managerName: string | null;
  /** Share of units with at least one active user — null = not computed yet (shown as «—»).
   *  «Følg opp» is derived client-side from `< 50`; it is never a stored status. */
  activationPercent: number | null;
}

/** Portfolio KPIs (`GET /api/admin/tenants/stats`); `series` = cumulative total per week, last 8 weeks. */
export interface PortfolioStats {
  total: number;
  active: number;
  newThisMonth: number;
  units: number;
  followUp: number;
  series: number[];
}

/** The tenant detail view (`GET /api/admin/tenants/{id}`). */
export interface TenantDetail {
  id: string;
  name: string;
  type: TenantTypeName;
  status: TenantStatusName;
  orgNumber: string | null;
  unitCount: number;
  plan: string;
  enabledModules: string[];
  provisionedBy: string | null;
  provisionedAtUtc: string | null;
  /** Board roles: members (status = membership status) + pending invitations (`invitert`). */
  roles: TenantRoleView[];
  /** Modules the plan ceiling keeps closed (`PlanCatalog`); the UI shows a lock. */
  lockedModules: string[];
}

/** One board role on a tenant (D4). */
export interface TenantRoleView {
  name: string;
  email: string | null;
  role: string;
  status: string;
}

/** A page of results plus the total (for portfolio paging). */
export interface PagedResult<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
}

export interface ProvisionAddress {
  street: string;
  postalCode: string;
  city: string;
}

export interface ProvisionBranding {
  displayName: string | null;
  accentColorToken: string | null;
}

/** Body for `POST /api/admin/tenants` (the provisioning wizard's submission). */
export interface ProvisionTenantRequest {
  name: string;
  type: string;
  orgNumber: string;
  addresses: ProvisionAddress[];
  unitCount: number;
  enabledModules: string[];
  plan: string;
  branding?: ProvisionBranding | null;
}

/** Portfolio filter / paging. */
export interface PortfolioQuery {
  search?: string;
  status?: TenantStatusName;
  type?: TenantTypeName;
  page?: number;
  pageSize?: number;
}

/** Reconfigure (konfigurer) — only the supplied fields change. */
export interface ReconfigureRequest {
  name?: string | null;
  enabledModules?: string[] | null;
  plan?: string | null;
  branding?: ProvisionBranding | null;
}

/** A parsed CSV import row. */
export interface ImportRow {
  andelSeksjonNumber: string;
  unitLabel: string;
  email: string | null;
  phone: string | null;
  role: string;
}

/** A per-row import validation error. */
export interface CsvRowError {
  rowNumber: number;
  field: string;
  message: string;
}

export interface ImportValidationResult {
  rowCount: number;
  errors: CsvRowError[];
  valid: boolean;
}

export interface ImportCommitResult {
  unitsCreated: number;
  invitationsCreated: number;
  alreadyCommitted: boolean;
  /** Rows dropped by `skipInvalid` (0 otherwise). */
  skippedRows: number;
}

/** A revisjonslogg entry. Names are resolved at read time (null when unknown); hashes expose the chain (A5). */
export interface AuditEntry {
  ts: string;
  actor: string;
  action: string;
  targetType: string;
  targetId: string | null;
  accessedTenantId: string | null;
  outcome: string;
  actorName: string | null;
  targetName: string | null;
  prevHash: string;
  entryHash: string;
}

/** `GET /api/admin/audit/verify` — does the hash chain recompute, and over how many entries. */
export interface AuditChainStatus {
  ok: boolean;
  count: number;
}

/** One row of the platform email log (`GET /api/admin/epost`, V1.13). Metadata only — no body. */
export interface EmailDelivery {
  id: string;
  tenantId: string | null;
  messageType: string;
  recipient: string;
  subject: string;
  status: string;
  brevoMessageId: string | null;
  error: string | null;
  sourceCollection: string | null;
  sourceId: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
  /** Stamped by the Brevo webhook (first delivery / first open / first click). */
  deliveredAtUtc?: string | null;
  openedAtUtc?: string | null;
  clickedAtUtc?: string | null;
}

/** One entry of the mail template catalogue (`GET /api/admin/epost/maler`). */
export interface EmailTemplateInfo {
  name: string;
  /** Badge group: auth | invitation | varsel | support | lead | test. */
  group: string;
  languages: string[];
  /** The `{{…}}` keys the sending code supplies (texts and layout built-ins excluded). */
  placeholders: string[];
  /** The sample value per placeholder (what a preview/test uses unless overridden). */
  samples: Record<string, string>;
}

/** A rendered template (`GET /api/admin/epost/maler/{name}?lang=`) — sample data, never real persons. */
export interface RenderedMail {
  subject: string;
  html: string;
}

export interface EmailSuppression {
  email: string;
  reason: string;
  createdAtUtc: string;
}

export interface BrevoBlockedContact {
  email: string;
  blockedAt: string | null;
  reasonCode: string | null;
  reasonMessage: string | null;
}

/** The local suppression list next to what Brevo itself blocks (`GET /api/admin/epost/sperreliste`). */
export interface SuppressionList {
  local: EmailSuppression[];
  brevo: { configured: boolean; contacts: BrevoBlockedContact[] };
}

/** A transactional webhook on the Brevo account (`GET /api/admin/brevo/webhooks`). */
export interface BrevoWebhook {
  id: number;
  url: string;
  description: string | null;
  events: string[];
  batched: boolean;
  createdAt: string | null;
}

export interface BrevoWebhooks {
  configured: boolean;
  webhooks: BrevoWebhook[];
}

/** Brevo's own totals for the whole period (`/smtp/statistics/aggregatedReport`, up to 90 days). */
export interface BrevoTotals {
  requests: number;
  delivered: number;
  opens: number;
  uniqueOpens: number;
  clicks: number;
  uniqueClicks: number;
  hardBounces: number;
  softBounces: number;
  blocked: number;
  spamReports: number;
  invalid: number;
  unsubscribed: number;
}

/** One day of Brevo's transactional counters (`GET /api/admin/brevo/statistikk`). */
export interface BrevoDailyStats {
  date: string;
  requests: number;
  delivered: number;
  uniqueOpens: number;
  uniqueClicks: number;
  hardBounces: number;
  softBounces: number;
  blocked: number;
  spamReports: number;
  invalid: number;
  deferred: number;
  unsubscribed: number;
}

/** Per-day Brevo counters for a range. `configured=false` ⇒ the API has no Brevo key (log-only mode). */
export interface BrevoStatistics {
  configured: boolean;
  rangeStart: string;
  rangeEnd: string;
  /** Whole-period totals (≤ 90 days). */
  totals: BrevoTotals;
  /** Per-day rows for the last ≤ 30 days of the period (Brevo's per-day cap). */
  daily: BrevoDailyStats[];
}

/** One message as Brevo sees it (`GET /api/admin/brevo/hendelser`) — grouped from its raw events. */
export interface BrevoActivityRow {
  messageId: string;
  from: string | null;
  recipient: string;
  subject: string | null;
  sentAt: string;
  /** A Brevo event name: requests | delivered | opened | clicks | hardBounces | softBounces | blocked | spam | invalid | deferred | unsubscribed | error. */
  status: string;
  deliveredAt: string | null;
  openedAt: string | null;
  clickedAt: string | null;
  reason: string | null;
  tags: string[];
}

/** Recent messages, newest first. `truncated` ⇒ Brevo had more events than one page holds. */
export interface BrevoActivity {
  configured: boolean;
  messages: BrevoActivityRow[];
  truncated: boolean;
}

/** Brevo account owner + plan credits (`GET /api/admin/brevo/konto`). */
export interface BrevoAccount {
  configured: boolean;
  email: string | null;
  companyName: string | null;
  plans: { type: string | null; creditsType: string | null; credits: number; startDate: string | null; endDate: string | null }[];
}

/** A message's Brevo timeline + the HTML that was sent (`GET /api/admin/brevo/melding?messageId=`). */
export interface BrevoMessage {
  configured: boolean;
  events: { date: string; event: string; reason: string | null; link: string | null }[];
  html: string | null;
}

/** Filter for the email log list. */
export interface EmailLogQuery {
  tenantId?: string;
  type?: string;
  status?: string;
  q?: string;
  limit?: number;
}

/**
 * Typed client for the platform-admin tenant API (`/api/admin`). Requests carry
 * the bearer token via the auth interceptor; the API enforces the PlatformAdmin
 * (AAL3) policy. Hand-written to mirror the backend contract until an OpenAPI
 * generator is wired.
 */
@Injectable({ providedIn: 'root' })
export class TenantAdminService {
  private readonly http = inject(HttpClient);
  private static readonly Base = '/api/admin';

  listTenants(query: PortfolioQuery = {}): Observable<PagedResult<TenantSummary>> {
    const params = toHttpParams(query);
    return this.http.get<PagedResult<TenantSummary>>(`${TenantAdminService.Base}/tenants`, { params });
  }

  provisionTenant(body: ProvisionTenantRequest): Observable<{ id: string }> {
    return this.http.post<{ id: string }>(`${TenantAdminService.Base}/tenants`, body);
  }

  changeStatus(id: string, to: TenantStatusName, reason?: string): Observable<void> {
    return this.http.post<void>(`${TenantAdminService.Base}/tenants/${encodeURIComponent(id)}/status`, { to, reason });
  }

  reconfigure(id: string, body: ReconfigureRequest): Observable<void> {
    return this.http.patch<void>(`${TenantAdminService.Base}/tenants/${encodeURIComponent(id)}`, body);
  }

  validateImport(id: string, rows: ImportRow[]): Observable<ImportValidationResult> {
    return this.http.post<ImportValidationResult>(`${TenantAdminService.Base}/tenants/${encodeURIComponent(id)}/import/validate`, { rows });
  }

  /** `skipInvalid` (D5) drops the rows validation flags and commits the rest; default = reject the batch. */
  commitImport(id: string, batchId: string, rows: ImportRow[], skipInvalid = false): Observable<ImportCommitResult> {
    return this.http.post<ImportCommitResult>(`${TenantAdminService.Base}/tenants/${encodeURIComponent(id)}/import/commit`, { batchId, rows, skipInvalid });
  }

  appointRole(id: string, email: string, role: string): Observable<void> {
    return this.http.post<void>(`${TenantAdminService.Base}/tenants/${encodeURIComponent(id)}/roles`, { email, role });
  }

  /** Consent-gated support impersonation — 501 until the 0.9 consent log lands. */
  supportSession(id: string): Observable<void> {
    return this.http.post<void>(`${TenantAdminService.Base}/tenants/${encodeURIComponent(id)}/support-session`, {});
  }

  /** Recomputes the audit hash chain server-side (read-only). */
  verifyAudit(): Observable<AuditChainStatus> {
    return this.http.get<AuditChainStatus>(`${TenantAdminService.Base}/audit/verify`);
  }

  /** Send a test email through the gateway (logs a row; real send when a Brevo key is configured). */
  sendTestEmail(to: string): Observable<TestEmailResult> {
    return this.http.post<TestEmailResult>(`${TenantAdminService.Base}/epost/test`, { to });
  }

  /** Send one template (logged as `test.<name>`); `values` override the samples, a `…Html` value is plain text. */
  sendTemplateTest(name: string, to: string, lang: string, values: Record<string, string> = {}): Observable<TestEmailResult> {
    return this.http.post<TestEmailResult>(`${TenantAdminService.Base}/epost/maler/${encodeURIComponent(name)}/test`, { to, lang, values });
  }

  /** «Fjern sperre»: drops the address locally and asks Brevo to unblock it. */
  unsuppress(email: string): Observable<void> {
    return this.http.delete<void>(`${TenantAdminService.Base}/epost/sperreliste/${encodeURIComponent(email)}`);
  }

  /** Creates or updates Brevo's transactional webhook pointing at `url` (must end in /api/brevo/webhook). */
  registerWebhook(url: string): Observable<{ id: number }> {
    return this.http.post<{ id: number }>(`${TenantAdminService.Base}/brevo/webhooks`, { url });
  }

  // ── SMS (V1.14) ──

  /** Send a test SMS through the gateway (sender + recipient + message). */
  sendTestSms(sender: string, to: string, content: string): Observable<TestSmsResult> {
    return this.http.post<TestSmsResult>(`${TenantAdminService.Base}/sms/test`, { sender, to, content });
  }

  /** Set (or clear, when null/0) a tenant's monthly SMS cap. */
  setSmsBudget(tenantId: string, monthlyLimit: number | null): Observable<void> {
    return this.http.put<void>(`${TenantAdminService.Base}/sms/budgets/${encodeURIComponent(tenantId)}`, { monthlyLimit });
  }
}

/** Outcome of a test send (`POST /api/admin/epost/test`). */
export interface TestEmailResult {
  sent: boolean;
  providerMessageId: string | null;
  error: string | null;
}

/** One row of the platform SMS log (`GET /api/admin/sms`, V1.14). Body-light (preview only). */
export interface SmsDelivery {
  id: string;
  tenantId: string | null;
  messageType: string;
  recipient: string;
  preview: string;
  status: string;
  brevoMessageId: string | null;
  segments: number;
  error: string | null;
  sourceCollection: string | null;
  sourceId: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

/** Filter for the SMS log list. */
export interface SmsLogQuery {
  tenantId?: string;
  type?: string;
  status?: string;
  q?: string;
  limit?: number;
}

/** A per-tenant monthly SMS cap + this month's usage. */
export interface SmsBudget {
  tenantId: string;
  monthlyLimit: number | null;
  usedThisMonth: number;
}

/** Outcome of a test SMS (`POST /api/admin/sms/test`). */
export interface TestSmsResult {
  sent: boolean;
  providerMessageId: string | null;
  error: string | null;
  segments: number;
  unicode: boolean;
}
